/**
 * 숫자 포맷팅 유틸리티
 * 회계처리 표준에 맞는 천단위 콤마 표시
 */

import { colors } from '../styles/designTokens';
import { formatDateTimeKST } from './dateUtils';

// ===== 타입 정의 =====
export type NumberFormatOptions = {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    useGrouping?: boolean;
};

// ===== 기본 포맷팅 옵션 =====
const DEFAULT_OPTIONS: NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
};

// ===== 숫자 포맷팅 함수 =====
export const formatNumber = (
    num: number | string | null | undefined,
    options: NumberFormatOptions = {}
): string => {
    // null, undefined, 빈 문자열 처리
    if (num === null || num === undefined || num === '') {
        return '0';
    }

    // 문자열을 숫자로 변환
    const numericValue = typeof num === 'string' ? parseFloat(num) : num;

    // NaN 처리
    if (isNaN(numericValue)) {
        return '0';
    }

    // 포맷팅 옵션 병합
    const formatOptions = { ...DEFAULT_OPTIONS, ...options };

    // 한국어 로케일로 포맷팅
    return numericValue.toLocaleString('ko-KR', formatOptions);
};

// ===== 금액 포맷팅 함수 =====
export const formatCurrency = (
    amount: number | string | null | undefined,
    currency: string = '원',
    options: NumberFormatOptions = {}
): string => {
    const formattedNumber = formatNumber(amount, options);
    return `${formattedNumber}${currency}`;
};

// ===== 퍼센티지 포맷팅 함수 =====
export const formatPercentage = (
    value: number | string | null | undefined,
    options: NumberFormatOptions = { minimumFractionDigits: 1, maximumFractionDigits: 1 }
): string => {
    const formattedNumber = formatNumber(value, options);
    return `${formattedNumber}%`;
};

// ===== 카운트 포맷팅 함수 (건수, 개수 등) =====
export const formatCount = (
    count: number | string | null | undefined,
    unit: string = '건'
): string => {
    const formattedNumber = formatNumber(count);
    return `${formattedNumber}${unit}`;
};

// ===== 큰 숫자 포맷팅 함수 (K, M, B 단위) =====
export const formatLargeNumber = (
    num: number | string | null | undefined,
    options: NumberFormatOptions = {}
): string => {
    if (num === null || num === undefined || num === '') {
        return '0';
    }

    const numericValue = typeof num === 'string' ? parseFloat(num) : num;

    if (isNaN(numericValue)) {
        return '0';
    }

    const absValue = Math.abs(numericValue);

    if (absValue >= 1000000000) {
        // 10억 이상
        const formatted = (numericValue / 1000000000).toLocaleString('ko-KR', {
            ...DEFAULT_OPTIONS,
            ...options,
            maximumFractionDigits: 1,
        });
        return `${formatted}B`;
    } else if (absValue >= 1000000) {
        // 100만 이상
        const formatted = (numericValue / 1000000).toLocaleString('ko-KR', {
            ...DEFAULT_OPTIONS,
            ...options,
            maximumFractionDigits: 1,
        });
        return `${formatted}M`;
    } else if (absValue >= 1000) {
        // 1천 이상
        const formatted = (numericValue / 1000).toLocaleString('ko-KR', {
            ...DEFAULT_OPTIONS,
            ...options,
            maximumFractionDigits: 1,
        });
        return `${formatted}K`;
    } else {
        // 1천 미만
        return formatNumber(numericValue, options);
    }
};

// ===== 금액 단위별 포맷팅 함수 =====
export const formatCurrencyWithUnit = (
    amount: number | string | null | undefined,
    options: NumberFormatOptions = {}
): string => {
    if (amount === null || amount === undefined || amount === '') {
        return '0원';
    }

    const numericValue = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numericValue)) {
        return '0원';
    }

    const absValue = Math.abs(numericValue);

    if (absValue >= 100000000) {
        // 1억 이상
        const formatted = (numericValue / 100000000).toLocaleString('ko-KR', {
            ...DEFAULT_OPTIONS,
            ...options,
            maximumFractionDigits: 1,
        });
        return `${formatted}억원`;
    } else if (absValue >= 10000) {
        // 1만 이상
        const formatted = (numericValue / 10000).toLocaleString('ko-KR', {
            ...DEFAULT_OPTIONS,
            ...options,
            maximumFractionDigits: 1,
        });
        return `${formatted}만원`;
    } else {
        // 1만 미만
        return formatCurrency(numericValue, '원', options);
    }
};

// ===== 변화량 포맷팅 함수 =====
export const formatChange = (
    value: number | string | null | undefined,
    options: NumberFormatOptions = {}
): { text: string; color: string; icon: string } => {
    if (value === null || value === undefined || value === '') {
        return { text: '0', color: colors.semantic.text.tertiary, icon: '→' };
    }

    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numericValue)) {
        return { text: '0', color: colors.semantic.text.tertiary, icon: '→' };
    }

    if (numericValue > 0) {
        return {
            text: `+${formatNumber(numericValue, options)}`,
            color: colors.success[600],
            icon: '↗'
        };
    } else if (numericValue < 0) {
        return {
            text: formatNumber(numericValue, options),
            color: colors.error[600],
            icon: '↘'
        };
    } else {
        return {
            text: '0',
            color: colors.semantic.text.tertiary,
            icon: '→'
        };
    }
};

// ===== 카드번호 마스킹 함수 =====
export const maskCardNumber = (cardNumber: string): string => {
    if (!cardNumber || cardNumber.length < 8) return cardNumber;

    const cleaned = cardNumber.replace(/\D/g, ''); // 숫자만 추출

    if (cleaned.length >= 8) {
        const first4 = cleaned.slice(0, 4);
        const last4 = cleaned.slice(-4);
        return `${first4}-****-****-${last4}`;
    }

    return cardNumber;
};

// ===== 전화번호 포맷팅 함수 =====
export const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return '';

    const cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.length === 11) {
        // 010-1234-5678
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
        // 02-123-4567
        return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
    }

    return phoneNumber;
};

// ===== 압축된 날짜 문자열 파싱 함수 =====
export const parseCompressedDate = (compressedDate: string): string => {
    if (!compressedDate || compressedDate.length !== 12) {
        return compressedDate || 'N/A';
    }

    try {
        const yy = compressedDate.slice(0, 2);
        const month = compressedDate.slice(2, 4);
        const day = compressedDate.slice(4, 6);
        const hour = compressedDate.slice(6, 8);
        const minute = compressedDate.slice(8, 10);

        return `${yy}.${month}.${day} ${hour}:${minute}`;
    } catch {
        return compressedDate;
    }
};

// ===== 날짜 포맷팅 함수 (표시: yy.mm.dd HH:mm, KST) =====
export const formatDate = (dateString: string | Date): string => {
    if (dateString === null || dateString === undefined || dateString === '') return 'N/A';

    try {
        if (typeof dateString === 'string' && /^\d{12}$/.test(dateString)) {
            return parseCompressedDate(dateString);
        }

        const iso =
            typeof dateString === 'string' ? dateString : dateString.toISOString();
        return formatDateTimeKST(iso);
    } catch {
        return typeof dateString === 'string' ? dateString : 'N/A';
    }
};

// ===== 기본 export =====
const numberFormatter = {
    formatNumber,
    formatCurrency,
    formatPercentage,
    formatCount,
    formatLargeNumber,
    formatCurrencyWithUnit,
    formatChange,
    maskCardNumber,
    formatPhoneNumber,
    parseCompressedDate,
    formatDate,
};

export default numberFormatter;
